import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Plus, Trash2, Play, CheckCircle, XCircle, RefreshCw, ExternalLink, Image, Type, ThumbsUp, ThumbsDown, ChevronLeft, ChevronRight, Search, X } from 'lucide-react'
import { aiGeneratorAPI } from '../../services/api'
import { toUtcDate } from '../../utils/dateFormat'
import OccasionScheduler from '../../components/OccasionScheduler/OccasionScheduler'
import { formatOccasionDisplay } from '../../utils/occasionUtils'
import PhotosSidebar from '../../editor/components/PhotosSidebar'

// Field types whose values are provided manually at run time (not AI-generated).
const isStaticRuntimeType = (t) => String(t || '').startsWith('static_runtime_')

const STATUS_STYLE = {
  pending: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Pending' },
  processing: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Processing' },
  awaiting_approval: { bg: 'bg-amber-100', text: 'text-amber-800', label: 'Awaiting Approval' },
  completed: { bg: 'bg-green-100', text: 'text-green-700', label: 'Completed' },
  failed: { bg: 'bg-red-100', text: 'text-red-700', label: 'Failed' },
  denied: { bg: 'bg-gray-200', text: 'text-gray-600', label: 'Denied' },
}

const PAGE_SIZE = 25
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

const AIExecutorPage = () => {
  const navigate = useNavigate()
  const { generatorId } = useParams()
  const [executors, setExecutors] = useState([])
  const [generator, setGenerator] = useState(null)
  const [loading, setLoading] = useState(true)
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [createForm, setCreateForm] = useState({ initial_input: '' })
  const [creating, setCreating] = useState(false)
  const occasionSchedulerRef = useRef(null)
  // Manual values for static_runtime_* fields, keyed by field name.
  const [staticValues, setStaticValues] = useState({})
  // When set to a field name, the Photos picker is open for that image field.
  const [imagePickerField, setImagePickerField] = useState(null)

  // The generator's static_runtime_* fields (require manual input at run time).
  const staticFields = (generator?.fields || []).filter(f => isStaticRuntimeType(f.fieldType))
  const [selectedExecutor, setSelectedExecutor] = useState(null)
  const [syncing, setSyncing] = useState(false)
  const [actioning, setActioning] = useState(false)
  const [statusFilter, setStatusFilter] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [pageSize, setPageSize] = useState(PAGE_SIZE)
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0, totalPages: 0, hasNext: false, hasPrev: false })
  // Multi-select for bulk delete: a Set of selected executor ids
  const [selectedIds, setSelectedIds] = useState(() => new Set())
  const [bulkDeleting, setBulkDeleting] = useState(false)
  const [bulkSyncing, setBulkSyncing] = useState(false)
  const [bulkRetrying, setBulkRetrying] = useState(false)
  // Field id currently being retried (partial retry), for per-field spinner.
  const [retryingFieldId, setRetryingFieldId] = useState(null)

  useEffect(() => {
    if (generatorId) fetchGenerator()
  }, [generatorId])

  useEffect(() => {
    fetchExecutors()
  }, [generatorId, pagination.page, pageSize, statusFilter, search])

  // Debounce the search box so we don't fire a request on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim())
      setPagination(prev => ({ ...prev, page: 1 }))
    }, 400)
    return () => clearTimeout(t)
  }, [searchInput])

  const fetchGenerator = async () => {
    try {
      const res = await aiGeneratorAPI.getById(generatorId)
      if (res.data.status === 'success') setGenerator(res.data.data)
    } catch (e) { console.error(e) }
  }

  const fetchExecutors = async () => {
    try {
      setLoading(true)
      const params = {
        page: pagination.page,
        limit: pageSize,
        ...(generatorId && { ai_generator_id: generatorId }),
        ...(statusFilter && { status: statusFilter }),
        ...(search && { search }),
      }
      const res = await aiGeneratorAPI.getExecutors(params)
      if (res.data.status === 'success') {
        setExecutors(res.data.data.items)
        setPagination(prev => ({ ...prev, ...res.data.data.pagination }))
        // Drop any selected ids that are no longer on the current page
        setSelectedIds(prev => {
          const visible = new Set(res.data.data.items.map(i => i.id))
          const next = new Set([...prev].filter(id => visible.has(id)))
          return next
        })
      }
    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }

  const refreshSelected = async (id) => {
    try {
      const res = await aiGeneratorAPI.getExecutor(id)
      if (res.data.status === 'success') setSelectedExecutor(res.data.data)
    } catch (e) { console.error(e) }
  }

  const handleCreate = async () => {
    try {
      // Collect Occasion Scheduling config; validate it if enabled.
      let occasionConfig = null
      if (occasionSchedulerRef.current) {
        const cfg = occasionSchedulerRef.current.getConfig()
        if (cfg?.isOccasionDateApplicable) {
          if (!occasionSchedulerRef.current.validate()) {
            alert('Please fix the occasion scheduling configuration errors')
            return
          }
          occasionConfig = cfg
        }
      }

      // Validate that every static_runtime_* field has a value provided.
      const missing = staticFields
        .filter(f => !String(staticValues[f.fieldName] || '').trim())
        .map(f => f.fieldName)
      if (missing.length > 0) {
        alert(`Please provide a value for: ${missing.join(', ')}`)
        return
      }

      setCreating(true)
      const payload = { initial_input: createForm.initial_input }
      if (occasionConfig) payload.occasion_config = occasionConfig
      if (staticFields.length > 0) {
        // Only send values for the generator's static fields.
        const sv = {}
        staticFields.forEach(f => { sv[f.fieldName] = String(staticValues[f.fieldName] || '').trim() })
        payload.static_values = sv
      }

      const res = await aiGeneratorAPI.runGenerator(generatorId, payload)
      if (res.data.status === 'success') {
        setShowCreateDialog(false)
        setCreateForm({ initial_input: '' })
        setStaticValues({})
        setPagination(prev => ({ ...prev, page: 1 }))
        fetchExecutors()
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to run')
    } finally { setCreating(false) }
  }

  const handleDelete = async (id, withTemplate = false) => {
    const msg = withTemplate
      ? 'Delete this execution AND its generated template? This cannot be undone.'
      : 'Delete this execution?'
    if (!window.confirm(msg)) return
    try {
      if (withTemplate) {
        await aiGeneratorAPI.deleteExecutorWithTemplate(id)
      } else {
        await aiGeneratorAPI.deleteExecutor(id)
      }
      if (selectedExecutor?.id === id) setSelectedExecutor(null)
      fetchExecutors()
    } catch (e) { alert(e.response?.data?.detail || 'Failed to delete') }
  }

  // ---- Multi-select helpers ----
  const toggleSelected = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const allVisibleSelected = executors.length > 0 && executors.every(ex => selectedIds.has(ex.id))
  const someVisibleSelected = executors.some(ex => selectedIds.has(ex.id)) && !allVisibleSelected

  const toggleSelectAll = () => {
    setSelectedIds(prev => {
      if (allVisibleSelected) return new Set()
      return new Set(executors.map(ex => ex.id))
    })
  }

  const clearSelection = () => setSelectedIds(new Set())

  const handleBulkDelete = async (withTemplate = false) => {
    const ids = [...selectedIds]
    if (ids.length === 0) return
    const msg = withTemplate
      ? `Delete ${ids.length} execution(s) AND their generated templates? This cannot be undone.`
      : `Delete ${ids.length} execution(s)? This cannot be undone.`
    if (!window.confirm(msg)) return
    try {
      setBulkDeleting(true)
      const res = await aiGeneratorAPI.bulkDeleteExecutors(ids, withTemplate)
      const count = res.data?.data?.deletedCount ?? ids.length
      if (selectedExecutor && ids.includes(selectedExecutor.id)) setSelectedExecutor(null)
      clearSelection()
      // If we just deleted the whole page, step back a page when possible.
      setPagination(prev => {
        const remaining = (prev.total || 0) - count
        const maxPage = Math.max(1, Math.ceil(remaining / pageSize))
        return { ...prev, page: Math.min(prev.page, maxPage) }
      })
      fetchExecutors()
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to delete selected executions')
    } finally { setBulkDeleting(false) }
  }

  const handleBulkSync = async () => {
    const ids = [...selectedIds]
    if (ids.length === 0) return
    try {
      setBulkSyncing(true)
      const res = await aiGeneratorAPI.bulkSyncExecutors(ids)
      // Refresh the open detail panel if it was part of the batch.
      if (selectedExecutor && ids.includes(selectedExecutor.id)) {
        refreshSelected(selectedExecutor.id)
      }
      fetchExecutors()
      const d = res.data?.data
      if (d?.errors?.length) {
        alert(`Synced ${d.syncedCount} execution(s), ${d.errors.length} failed.`)
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to sync selected executions')
    } finally { setBulkSyncing(false) }
  }

  const handleBulkRetry = async () => {
    const ids = [...selectedIds]
    if (ids.length === 0) return
    if (!window.confirm(`Retry ${ids.length} execution(s)? Busy/completed ones are skipped.`)) return
    try {
      setBulkRetrying(true)
      const res = await aiGeneratorAPI.bulkRetryExecutors(ids)
      if (selectedExecutor && ids.includes(selectedExecutor.id)) {
        refreshSelected(selectedExecutor.id)
      }
      fetchExecutors()
      const d = res.data?.data
      if (d) {
        let msg = `Retried ${d.retriedCount} execution(s)`
        if (d.skippedCount) msg += `, skipped ${d.skippedCount}`
        if (d.errors?.length) msg += `, ${d.errors.length} failed`
        if (d.skippedCount || d.errors?.length) alert(msg)
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to retry selected executions')
    } finally { setBulkRetrying(false) }
  }

  const handleSelect = (executor) => refreshSelected(executor.id)

  const handleSync = async () => {
    if (!selectedExecutor) return
    try {
      setSyncing(true)
      const res = await aiGeneratorAPI.syncExecutor(selectedExecutor.id)
      if (res.data.status === 'success') {
        setSelectedExecutor(res.data.data)
        fetchExecutors()
      }
    } catch (e) { console.error(e) }
    finally { setSyncing(false) }
  }

  const handleRetry = async () => {
    if (!selectedExecutor) return
    try {
      const res = await aiGeneratorAPI.retryExecutor(selectedExecutor.id)
      if (res.data.status === 'success') {
        setSelectedExecutor(res.data.data)
        fetchExecutors()
      }
    } catch (e) { alert(e.response?.data?.detail || 'Failed') }
  }

  // Partial retry: re-run a single failed image field.
  const handleRetryField = async (fieldId) => {
    if (!selectedExecutor) return
    try {
      setRetryingFieldId(fieldId)
      const res = await aiGeneratorAPI.retryExecutorField(selectedExecutor.id, fieldId)
      if (res.data.status === 'success') {
        setSelectedExecutor(res.data.data)
        fetchExecutors()
      }
    } catch (e) { alert(e.response?.data?.detail || 'Failed to retry field') }
    finally { setRetryingFieldId(null) }
  }

  const handleApprove = async () => {
    if (!selectedExecutor) return
    try {
      setActioning(true)
      const res = await aiGeneratorAPI.approveExecutor(selectedExecutor.id)
      if (res.data.status === 'success') {
        setSelectedExecutor(res.data.data)
        fetchExecutors()
      }
    } catch (e) { alert(e.response?.data?.detail || 'Failed to approve') }
    finally { setActioning(false) }
  }

  const handleDeny = async () => {
    if (!selectedExecutor) return
    if (!window.confirm('Deny this execution? No template will be created.')) return
    try {
      setActioning(true)
      const res = await aiGeneratorAPI.denyExecutor(selectedExecutor.id)
      if (res.data.status === 'success') {
        setSelectedExecutor(res.data.data)
        fetchExecutors()
      }
    } catch (e) { alert(e.response?.data?.detail || 'Failed to deny') }
    finally { setActioning(false) }
  }

  const StatusBadge = ({ status }) => {
    const s = STATUS_STYLE[status] || STATUS_STYLE.pending
    return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${s.bg} ${s.text}`}>{s.label}</span>
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/dashboard/ai-generator')} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="text-xl font-bold text-gray-900">AI Executions</h1>
            {generator && <p className="text-sm text-gray-500">{generator.name}</p>}
          </div>
        </div>
        {generatorId && (
          <button onClick={() => { setStaticValues({}); setShowCreateDialog(true) }} className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm">
            <Plus className="w-4 h-4" /> Run
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left: Executor List */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {/* Filters */}
            <div className="px-4 py-3 bg-gray-50 border-b space-y-2.5">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-semibold text-gray-500 uppercase">
                  Executions ({pagination.total})
                </span>
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setPagination(prev => ({ ...prev, page: 1 })) }}
                  className="text-xs border border-gray-300 rounded px-2 py-1 bg-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="awaiting_approval">Awaiting Approval</option>
                  <option value="completed">Completed</option>
                  <option value="failed">Failed</option>
                  <option value="denied">Denied</option>
                </select>
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search by input text..."
                  className="w-full text-xs border border-gray-300 rounded pl-8 pr-7 py-1.5 bg-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
                {searchInput && (
                  <button
                    onClick={() => setSearchInput('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    title="Clear search"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>

              {/* Select-all + page size */}
              <div className="flex items-center justify-between gap-2">
                <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    ref={el => { if (el) el.indeterminate = someVisibleSelected }}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                  />
                  Select all
                </label>
                <div className="flex items-center gap-1.5 text-xs text-gray-500">
                  <span>Per page</span>
                  <select
                    value={pageSize}
                    onChange={(e) => { setPageSize(Number(e.target.value)); setPagination(prev => ({ ...prev, page: 1 })) }}
                    className="border border-gray-300 rounded px-1.5 py-1 bg-white focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  >
                    {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Bulk action toolbar */}
            {selectedIds.size > 0 && (
              <div className="px-4 py-2.5 bg-orange-50 border-b border-orange-100 flex items-center justify-between gap-2 flex-wrap">
                <span className="text-xs font-medium text-orange-800">{selectedIds.size} selected</span>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={handleBulkSync}
                    disabled={bulkSyncing || bulkRetrying || bulkDeleting}
                    className="px-2.5 py-1 text-xs border border-blue-300 bg-white text-blue-700 rounded hover:bg-blue-50 disabled:opacity-50 flex items-center gap-1"
                    title="Poll workflow status for selected executions"
                  >
                    <RefreshCw className={`w-3 h-3 ${bulkSyncing ? 'animate-spin' : ''}`} /> {bulkSyncing ? 'Syncing...' : 'Sync'}
                  </button>
                  <button
                    onClick={handleBulkRetry}
                    disabled={bulkRetrying || bulkSyncing || bulkDeleting}
                    className="px-2.5 py-1 text-xs border border-purple-300 bg-white text-purple-700 rounded hover:bg-purple-50 disabled:opacity-50 flex items-center gap-1"
                    title="Retry selected executions (partial-aware; busy/completed skipped)"
                  >
                    <RefreshCw className={`w-3 h-3 ${bulkRetrying ? 'animate-spin' : ''}`} /> {bulkRetrying ? 'Retrying...' : 'Retry'}
                  </button>
                  <button
                    onClick={() => handleBulkDelete(false)}
                    disabled={bulkDeleting}
                    className="px-2.5 py-1 text-xs border border-gray-300 bg-white text-gray-700 rounded hover:bg-gray-50 disabled:opacity-50 flex items-center gap-1"
                    title="Delete selected executions only"
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                  <button
                    onClick={() => handleBulkDelete(true)}
                    disabled={bulkDeleting}
                    className="px-2.5 py-1 text-xs bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50 flex items-center gap-1"
                    title="Delete selected executions and their generated templates"
                  >
                    <Trash2 className="w-3 h-3" /> {bulkDeleting ? 'Deleting...' : 'Delete + Template'}
                  </button>
                  <button
                    onClick={clearSelection}
                    disabled={bulkDeleting}
                    className="p-1 text-orange-700 hover:text-orange-900 disabled:opacity-50"
                    title="Clear selection"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            )}

            {loading ? (
              <div className="p-6 text-center text-gray-400 text-sm">Loading...</div>
            ) : executors.length === 0 ? (
              <div className="p-6 text-center text-gray-400 text-sm">No executions</div>
            ) : (
              <div className="divide-y divide-gray-100 max-h-[65vh] overflow-y-auto">
                {executors.map((ex) => {
                  const isSelected = selectedIds.has(ex.id)
                  return (
                    <div
                      key={ex.id}
                      onClick={() => handleSelect(ex)}
                      className={`px-4 py-3 cursor-pointer hover:bg-gray-50 transition flex gap-2.5 ${selectedExecutor?.id === ex.id ? 'bg-orange-50 border-l-4 border-l-orange-500' : ''} ${isSelected ? 'bg-orange-50/60' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onClick={(e) => e.stopPropagation()}
                        onChange={() => toggleSelected(ex.id)}
                        className="mt-0.5 rounded border-gray-300 text-orange-600 focus:ring-orange-500 flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <StatusBadge status={ex.status} />
                          <button onClick={(e) => { e.stopPropagation(); handleDelete(ex.id) }} className="p-1 text-gray-400 hover:text-red-500" title="Delete execution">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                        {ex.initialInput && (
                          <p className="text-xs text-gray-600 mt-1 line-clamp-2">{ex.initialInput}</p>
                        )}
                        <p className="text-[10px] text-gray-400 mt-1">{toUtcDate(ex.createdAt).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true })}</p>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}

            {/* Pagination controls */}
            <div className="px-4 py-3 bg-gray-50 border-t flex items-center justify-between">
              <span className="text-[11px] text-gray-500">
                {pagination.total > 0 ? (
                  <>
                    {(pagination.page - 1) * pageSize + 1}
                    –{Math.min(pagination.page * pageSize, pagination.total)} of {pagination.total}
                  </>
                ) : (
                  <>Page {pagination.page} of {pagination.totalPages || 1}</>
                )}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={!pagination.hasPrev}
                  className="p-1.5 border rounded disabled:opacity-40 hover:bg-gray-100"
                  title="Previous page"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                <span className="text-[11px] text-gray-500 px-1">{pagination.page}/{pagination.totalPages || 1}</span>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={!pagination.hasNext}
                  className="p-1.5 border rounded disabled:opacity-40 hover:bg-gray-100"
                  title="Next page"
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Right: Detail Panel */}
        <div className="lg:col-span-2">
          {!selectedExecutor ? (
            <div className="bg-white rounded-lg border border-gray-200 p-10 text-center text-gray-400">
              <Play className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Select an execution to view details</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              {/* Detail Header */}
              <div className="px-5 py-4 bg-gray-50 border-b flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <StatusBadge status={selectedExecutor.status} />
                  {selectedExecutor.currentJobType && (
                    <span className="text-xs text-blue-600 bg-blue-50 px-2 py-0.5 rounded">
                      {selectedExecutor.currentJobType}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {selectedExecutor.status === 'awaiting_approval' && (
                    <>
                      <button onClick={handleApprove} disabled={actioning} className="px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-1">
                        <ThumbsUp className="w-3 h-3" /> Approve
                      </button>
                      <button onClick={handleDeny} disabled={actioning} className="px-3 py-1.5 text-xs bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:opacity-50 flex items-center gap-1">
                        <ThumbsDown className="w-3 h-3" /> Deny
                      </button>
                    </>
                  )}
                  {(() => {
                    const hasFailedField = (selectedExecutor.fields || []).some(f => f.fieldStatus === 'failed')
                    // Retry is available when the executor has stopped and may need
                    // another attempt: pending, failed, or completed-with-failed-field.
                    const canRetry =
                      selectedExecutor.status === 'pending' ||
                      selectedExecutor.status === 'failed' ||
                      (selectedExecutor.status === 'completed' && hasFailedField)
                    return canRetry ? (
                      <button onClick={handleRetry} className="px-3 py-1.5 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-1" title="Retry (re-runs text if needed, otherwise only failed image fields)">
                        <RefreshCw className="w-3 h-3" /> Retry
                      </button>
                    ) : null
                  })()}
                  {selectedExecutor.status === 'processing' && (
                    <button onClick={handleSync} disabled={syncing} className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-1">
                      <RefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} /> Sync
                    </button>
                  )}
                  {selectedExecutor.outputTemplateId && (
                    <a
                      href={`/editor/${selectedExecutor.outputTemplateId}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-1"
                    >
                      Open Template <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  {/* Delete menu */}
                  <button
                    onClick={() => handleDelete(selectedExecutor.id, false)}
                    className="px-3 py-1.5 text-xs border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 flex items-center gap-1"
                    title="Delete execution only"
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                  {selectedExecutor.outputTemplateId && (
                    <button
                      onClick={() => handleDelete(selectedExecutor.id, true)}
                      className="px-3 py-1.5 text-xs bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-1"
                      title="Delete execution and its generated template"
                    >
                      <Trash2 className="w-3 h-3" /> Delete + Template
                    </button>
                  )}
                </div>
              </div>

              {/* Awaiting approval banner */}
              {selectedExecutor.status === 'awaiting_approval' && (
                <div className="px-5 py-3 bg-amber-50 border-b border-amber-100">
                  <p className="text-xs text-amber-800">
                    This execution is waiting for your approval. Approve to create the template, or Deny to discard.
                  </p>
                </div>
              )}

              {/* Initial Input */}
              {selectedExecutor.initialInput && (
                <div className="px-5 py-3 border-b bg-gray-50/50">
                  <p className="text-xs text-gray-500 mb-1">Input</p>
                  <p className="text-sm text-gray-800">{selectedExecutor.initialInput}</p>
                </div>
              )}

              {/* Occasion scheduling + category/subcategory saved for this execution */}
              {(() => {
                const occ = selectedExecutor.occasion
                const cat = selectedExecutor.category || {}
                const occText = occ ? formatOccasionDisplay(occ) : null
                const hasCategory = cat.categoryId || cat.subCategoryId
                if (!occ && !hasCategory) return null
                return (
                  <div className="px-5 py-3 border-b bg-indigo-50/40">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {/* Category / Subcategory */}
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Category</p>
                        {hasCategory ? (
                          <div className="text-sm text-gray-800 space-y-0.5">
                            <div>
                              <span className="text-gray-500">Category: </span>
                              {cat.categoryName || cat.categoryId || <span className="text-gray-400">—</span>}
                            </div>
                            <div>
                              <span className="text-gray-500">Subcategory: </span>
                              {cat.subCategoryName || cat.subCategoryId || <span className="text-gray-400">—</span>}
                            </div>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400">Not set</p>
                        )}
                      </div>

                      {/* Occasion scheduling */}
                      <div>
                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Occasion Scheduling</p>
                        {occ ? (
                          <div className="text-sm text-gray-800 space-y-0.5">
                            <div>
                              <span className="inline-block text-xs font-medium px-2 py-0.5 rounded bg-indigo-100 text-indigo-700">
                                {occ.occasionType}
                              </span>
                            </div>
                            {occText && <div className="text-gray-700">{occText}</div>}
                            {occ.occasionTimezone && (
                              <div className="text-xs text-gray-500">Timezone: {occ.occasionTimezone}</div>
                            )}
                          </div>
                        ) : (
                          <p className="text-sm text-gray-400">No occasion scheduling</p>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })()}

              {/* Error */}
              {selectedExecutor.errorMessage && (
                <div className="px-5 py-3 bg-red-50 border-b">
                  <p className="text-xs text-red-700">{selectedExecutor.errorMessage}</p>
                </div>
              )}

              {/* Fields */}
              <div className="p-5">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Generated Fields</h4>
                <div className="space-y-4">
                  {(selectedExecutor.fields || []).map((field) => (
                    <FieldDisplay
                      key={field.id}
                      field={field}
                      onRetry={() => handleRetryField(field.id)}
                      retrying={retryingFieldId === field.id}
                    />
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create Dialog */}
      {showCreateDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl shadow-xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Run AI Generator</h3>
            <label className="block text-xs font-medium text-gray-600 mb-1">Fixed input (sent to the AI)</label>
            <textarea
              value={createForm.initial_input}
              onChange={(e) => setCreateForm({ initial_input: e.target.value })}
              placeholder="Enter input (e.g., date, theme, occasion)..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm"
              autoFocus
            />

            {/* Manual values for static_runtime_* fields. These are provided by the
                user and injected directly (not generated by the AI). */}
            {staticFields.length > 0 && (
              <div className="mt-4 border border-emerald-200 bg-emerald-50/50 rounded-lg p-4">
                <p className="text-sm font-medium text-emerald-800 mb-1">Manual field values</p>
                <p className="text-xs text-emerald-700/80 mb-3">
                  These fields are not generated by AI. Provide their values for this run.
                </p>
                <div className="space-y-3">
                  {staticFields.map((f) => {
                    const val = staticValues[f.fieldName] || ''
                    const setVal = (v) => setStaticValues(prev => ({ ...prev, [f.fieldName]: v }))
                    return (
                      <div key={f.id}>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          {f.fieldName}
                          {f.fieldDescription && <span className="text-gray-400 font-normal"> — {f.fieldDescription}</span>}
                          <span className="ml-2 text-[10px] text-emerald-600">{f.fieldType}</span>
                        </label>

                        {f.fieldType === 'static_runtime_text' && (
                          <input
                            type="text"
                            value={val}
                            onChange={(e) => setVal(e.target.value)}
                            placeholder="Enter text..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                          />
                        )}

                        {f.fieldType === 'static_runtime_url' && (
                          <input
                            type="url"
                            value={val}
                            onChange={(e) => setVal(e.target.value)}
                            placeholder="https://example.com/image.png"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                          />
                        )}

                        {f.fieldType === 'static_runtime_image' && (
                          <div className="flex items-center gap-3">
                            {val ? (
                              <img src={val} alt={f.fieldName} className="w-16 h-16 object-cover rounded border bg-gray-50" />
                            ) : (
                              <div className="w-16 h-16 rounded border border-dashed border-gray-300 flex items-center justify-center text-gray-300">
                                <Image className="w-5 h-5" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              {val && <p className="text-xs text-gray-500 truncate mb-1">{val}</p>}
                              <div className="flex gap-2">
                                <button
                                  type="button"
                                  onClick={() => setImagePickerField(f.fieldName)}
                                  className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
                                >
                                  {val ? 'Change image' : 'Choose image'}
                                </button>
                                {val && (
                                  <button
                                    type="button"
                                    onClick={() => setVal('')}
                                    className="px-3 py-1.5 text-xs text-gray-600 border rounded-lg hover:bg-gray-50"
                                  >
                                    Clear
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Occasion Scheduling — prefilled from the generator's saved default;
                can be overridden for this run. Applied to every template created. */}
            <div className="mt-4">
              <OccasionScheduler
                key={showCreateDialog ? 'run-occasion-open' : 'run-occasion-closed'}
                ref={occasionSchedulerRef}
                value={generator?.occasion || {}}
              />
              <p className="text-xs text-gray-500 mt-2">
                {generator?.occasion?.isOccasionDateApplicable
                  ? 'Prefilled from this generator\u2019s default schedule. Adjust it to override for this run only.'
                  : 'When enabled, this schedule is saved on every template generated by this run \u2014 no need to schedule each template manually.'}
              </p>
            </div>

            <div className="flex justify-end gap-3 mt-4">
              <button onClick={() => setShowCreateDialog(false)} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={handleCreate} disabled={creating} className="px-4 py-2 text-sm bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50">
                {creating ? 'Running...' : 'Run'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photos picker for static_runtime_image fields. Reuses the editor's
          PhotosSidebar (supports choosing an existing photo or uploading a new
          one). onPhotoSelect returns the full photo; we store its permanent URL. */}
      {imagePickerField && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h3 className="text-sm font-semibold text-gray-900">
                Choose image for “{imagePickerField}”
              </h3>
              <button onClick={() => setImagePickerField(null)} className="p-1 text-gray-500 hover:bg-gray-100 rounded">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 min-h-0 relative">
              <PhotosSidebar
                isOpen={true}
                isDialog={true}
                onClose={() => setImagePickerField(null)}
                onPhotoSelect={(photo) => {
                  const url = photo?.storageUrl || photo?.url || photo?.thumbnailUrl || ''
                  if (url) {
                    setStaticValues(prev => ({ ...prev, [imagePickerField]: url }))
                  }
                  setImagePickerField(null)
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Field display component.
 * - Image fields (completed): image shown full on one side, prompt on the other (side-by-side).
 * - Text fields: readable text.
 */
const FieldDisplay = ({ field, onRetry, retrying = false }) => {
  const isAiImage = field.fieldType === 'image'
  // static_runtime_image / static_runtime_url hold an image/media URL provided
  // manually by the user; render them as an image like AI image fields.
  const isStaticImageLike = field.fieldType === 'static_runtime_image' || field.fieldType === 'static_runtime_url'
  const isImage = isAiImage
  const isCompleted = field.fieldStatus === 'completed'
  const statusStyle = STATUS_STYLE[field.fieldStatus] || STATUS_STYLE.pending

  // For completed image fields, the value is the image URL and the prompt is in fieldPrompt.
  const imageUrl = isImage && isCompleted ? field.fieldValue : null
  const imagePrompt = field.fieldPrompt || (isImage && !isCompleted ? field.fieldValue : '')

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      {/* Field Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b">
        <div className="flex items-center gap-2">
          {(isImage || isStaticImageLike) ? <Image className="w-3.5 h-3.5 text-purple-500" /> : <Type className="w-3.5 h-3.5 text-blue-500" />}
          <span className="text-sm font-medium text-gray-800">{field.fieldName}</span>
          <span className="text-[10px] text-gray-400">{field.fieldType}</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Per-field (partial) retry: only for failed AI image fields */}
          {isAiImage && field.fieldStatus === 'failed' && onRetry && (
            <button
              onClick={onRetry}
              disabled={retrying}
              className="px-2 py-0.5 rounded text-[10px] font-medium bg-purple-600 text-white hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1"
              title="Retry generating this image"
            >
              <RefreshCw className={`w-2.5 h-2.5 ${retrying ? 'animate-spin' : ''}`} />
              {retrying ? 'Retrying...' : 'Retry'}
            </button>
          )}
          <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${statusStyle.bg} ${statusStyle.text}`}>
            {statusStyle.label}
          </span>
        </div>
      </div>

      {/* Field Value */}
      <div className="p-3">
        {!field.fieldValue ? (
          <p className="text-xs text-gray-400 italic">No value yet</p>
        ) : isImage && isCompleted ? (
          // Side-by-side: image on the left (full), prompt on the right
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <img
                src={imageUrl}
                alt={field.fieldName}
                className="w-full rounded border object-contain bg-gray-50"
                style={{ maxHeight: '320px' }}
              />
              <a href={imageUrl} target="_blank" rel="noopener noreferrer" className="text-[11px] text-blue-500 hover:underline mt-1 inline-flex items-center gap-1">
                Open full size <ExternalLink className="w-3 h-3" />
              </a>
            </div>
            <div>
              <p className="text-[11px] font-medium text-gray-500 mb-1 uppercase tracking-wide">Prompt</p>
              <p className="text-sm text-gray-800 bg-purple-50 p-3 rounded whitespace-pre-wrap break-words">
                {imagePrompt || '(no prompt recorded)'}
              </p>
            </div>
          </div>
        ) : isImage && !isCompleted ? (
          <div>
            <p className="text-xs text-gray-500 italic mb-1">Image prompt:</p>
            <p className="text-sm text-gray-700 bg-purple-50 p-2 rounded whitespace-pre-wrap break-words">{imagePrompt}</p>
          </div>
        ) : isStaticImageLike ? (
          // Manually-provided image / URL value — show the image with a link.
          <div>
            <img
              src={field.fieldValue}
              alt={field.fieldName}
              className="w-full rounded border object-contain bg-gray-50"
              style={{ maxHeight: '320px' }}
              onError={(e) => { e.currentTarget.style.display = 'none' }}
            />
            <a href={field.fieldValue} target="_blank" rel="noopener noreferrer" className="text-[11px] text-blue-500 hover:underline mt-1 inline-flex items-center gap-1 break-all">
              {field.fieldValue} <ExternalLink className="w-3 h-3 flex-shrink-0" />
            </a>
          </div>
        ) : (
          <p className="text-sm text-gray-800 whitespace-pre-wrap break-words">{field.fieldValue}</p>
        )}
      </div>
    </div>
  )
}

export default AIExecutorPage
